import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth services
export const authService = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  }
};

// Attendance services
export const attendanceService = {
  // Mark as present for today (traditional model - must mark daily)
  markPresent: async (remarks = '') => {
    const response = await api.post('/attendance/mark-present', { remarks });
    return response.data;
  },

  // Mark as absent for a specific date
  markAbsent: async (date, remarks = '') => {
    const response = await api.post('/attendance/mark-absent', { date, remarks });
    return response.data;
  },

  // Mark multiple dates as absent
  markAbsentMultiple: async (dates, remarks = '') => {
    const response = await api.post('/attendance/mark-absent-multiple', { dates, remarks });
    return response.data;
  },

  // Remove absence (back to present)
  removeAbsent: async (date) => {
    const response = await api.delete(`/attendance/remove-absent/${date}`);
    return response.data;
  },

  getMyAttendance: async (month, year) => {
    const response = await api.get('/attendance/my-attendance', { params: { month, year } });
    return response.data;
  },

  checkTodayStatus: async () => {
    const response = await api.get('/attendance/today');
    return response.data;
  },

  // Admin services
  getAllUsers: async () => {
    const response = await api.get('/attendance/users');
    return response.data;
  },

  getMonthlyReport: async (month, year) => {
    const response = await api.get('/attendance/report/monthly', {
      params: { month, year }
    });
    return response.data;
  },

  // Get yearly attendance report
  getYearlyReport: async (year) => {
    const response = await api.get('/attendance/report/yearly', {
      params: { year }
    });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/attendance/stats');
    return response.data;
  },

  // Get today's detailed attendance log with check-in times
  getTodayLog: async () => {
    const response = await api.get('/attendance/today-log');
    return response.data;
  },

  // System Settings - Go-Live Date
  getGoLiveDate: async () => {
    const response = await api.get('/attendance/settings/go-live-date');
    return response.data;
  },

  setGoLiveDate: async (goLiveDate) => {
    const response = await api.post('/attendance/settings/go-live-date', { goLiveDate });
    return response.data;
  },

  clearGoLiveDate: async () => {
    const response = await api.delete('/attendance/settings/go-live-date');
    return response.data;
  },

  // Admin: Delete a member
  deleteMember: async (userId) => {
    const response = await api.delete(`/attendance/users/${userId}`);
    return response.data;
  }
};

export default api;
